import { createEvent, logOneDSDatapoint } from './OneDsWrapper';
import { AriaDatapoint } from './datapoints/AriaDatapoint';
import type { InternalAnalyticOptions } from './types/InternalAnalyticOptions';
import { AnalyticsOptions, DataPointEventType } from './types/DatapointEnums';
import { EventLatency, IEventProperty, ValueKind } from '@microsoft/1ds-analytics-js';
import { ecsClientSettings } from './initializeAnalytics';
import { addLegacyEventUnifiedProperties, buildUnifiedDataMap } from './utils/addUnifiedProperties';
import optionallyTraceDatapoint from './utils/optionallyTraceDatapoint';
import isDevEnvironment from './utils/isDevEnvironment';
import { matchLegacyMappingsAndCatalog, matchUserActionCatalog } from './utils/getEcsCatalogMatch';
import { checkAllRequiredProperties } from './utils/checkEcsCatalogProperties';

export function logUnifiedEvents(
    datapoint: AriaDatapoint,
    analyticsOptions: InternalAnalyticOptions,
    eventType: string
) {
    if (!isTargetEventType(eventType)) {
        return;
    }
    // For new unified events, determine if event matches UserActionCatalog
    const catalogMatch = matchUserActionCatalog(datapoint.eventName);
    if (catalogMatch.length > 0) {
        datapoint.properties = buildUnifiedDataMap(datapoint, catalogMatch[0]);
        if (checkAllRequiredProperties(datapoint, catalogMatch[0].requiredProperties)) {
            executeLoggingOptions(datapoint, analyticsOptions);
        }
    }
    // Legacy events in ECS mappings list that need to be remapped as unified events
    else {
        const legacyMatch = matchLegacyMappingsAndCatalog(datapoint);
        if (legacyMatch.length > 0) {
            datapoint.properties = buildUnifiedDataMap(datapoint, legacyMatch[0]);
            addLegacyEventUnifiedProperties(datapoint, legacyMatch[0]);
            if (checkAllRequiredProperties(datapoint, legacyMatch[0].requiredProperties)) {
                datapoint.eventName = legacyMatch[0].actionName;
                executeLoggingOptions(datapoint, analyticsOptions);
            }
        }
    }
}

function isTargetEventType(eventType: string): boolean {
    return (
        eventType === DataPointEventType.ClientEvent ||
        eventType === DataPointEventType.ClientCosmos
    );
}

function executeLoggingOptions(datapoint: AriaDatapoint, analyticsOptions: AnalyticsOptions) {
    optionallyTraceDatapoint(datapoint);

    // Do not log datapoints if we are gulping or in a branch or a tds box.
    if (!isDevEnvironment(analyticsOptions)) {
        logUnifiedEvent(
            ecsClientSettings.ingestionSettings.ingestionKey,
            datapoint,
            ecsClientSettings.userActionEventCatalog.eventType,
            analyticsOptions
        );
    }
}

function logUnifiedEvent(
    tenantToken: string,
    datapoint: AriaDatapoint,
    eventType: string,
    analyticsOptions: InternalAnalyticOptions
) {
    const e = createEvent(eventType, datapoint.properties);
    e.latency = EventLatency.Normal; // how quickly event is submitted to Aria
    if (e.data) {
        if (datapoint.piiData) {
            e.data['PiiData'] = <IEventProperty>{
                value: datapoint.piiData,
                kind: ValueKind.Pii_Identity,
            };
        }
    }

    logOneDSDatapoint({ tenantToken, item: e, analyticsOptions, useUnifiedSchema: true });
}
